// apps/web/src/pages/admin/reservations/ReservationsDatePicker.tsx — Quick 260803-ger.
//
// Collapsible month-grid date picker for the merged Reservations page.
// Owns the month state + 5 day-state CSS classes reused from
// calendar/CalendarPage.tsx. Tapping a day only updates the parent
// `?date=` (the explicit "Tag sperren" action still lives on the
// page level and uses CalendarDayTapDialog).

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { formatInTimeZone } from "date-fns-tz";
import { gqlRequest } from "../../../lib/api";
import { Lock } from "lucide-react";
import {
  AVAILABILITY_MONTH_QUERY,
  type DayStateKind,
} from "../calendar/calendar-operations";

interface DayStateCell {
  date: string;
  state: DayStateKind;
  reasons: string[];
}

function currentMonthString(date: Date): string {
  const tz = "Europe/Berlin";
  const yyyy = formatInTimeZone(date, tz, "yyyy");
  const mm = formatInTimeZone(date, tz, "MM");
  return `${yyyy}-${mm}`;
}

function shiftMonth(yyyyMm: string, delta: number): string {
  const m = /^(\d{4})-(\d{2})$/.exec(yyyyMm);
  if (!m) return yyyyMm;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const next = new Date(Date.UTC(year, month - 1 + delta, 1));
  return `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, "0")}`;
}

function formatMonthLabel(yyyyMm: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(yyyyMm);
  if (!m) return yyyyMm;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const dt = new Date(Date.UTC(year, month - 1, 1));
  return dt.toLocaleDateString("de-DE", {
    year: "numeric",
    month: "long",
    timeZone: "UTC",
  });
}

function buildGridCells(
  month: string,
  cells: DayStateCell[],
): Array<{
  date: string;
  state: DayStateKind;
  inMonth: boolean;
}> {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) return [];
  const year = Number(m[1]);
  const monthIdx = Number(m[2]);
  const firstOfMonth = new Date(Date.UTC(year, monthIdx - 1, 1));
  const jsDow = firstOfMonth.getUTCDay();
  const isoDow = jsDow === 0 ? 7 : jsDow;
  const daysInMonth = new Date(Date.UTC(year, monthIdx, 0)).getUTCDate();
  const cellMap = new Map<string, DayStateKind>();
  for (const c of cells) {
    cellMap.set(c.date, c.state);
  }
  const cellsOut: Array<{
    date: string;
    state: DayStateKind;
    inMonth: boolean;
  }> = [];
  const prevMonthYear = monthIdx === 1 ? year - 1 : year;
  const prevMonthIdx1Indexed = monthIdx === 1 ? 12 : monthIdx - 1;
  const prevDays = new Date(Date.UTC(year, monthIdx - 1, 0)).getUTCDate();
  for (let i = 1; i < isoDow; i++) {
    const padDay = prevDays - (isoDow - 1 - i);
    cellsOut.push({
      date: `${prevMonthYear}-${String(prevMonthIdx1Indexed).padStart(2, "0")}-${String(padDay).padStart(2, "0")}`,
      state: "OUTSIDE_WINDOW",
      inMonth: false,
    });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const date = `${year}-${String(monthIdx).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cellsOut.push({
      date,
      state: cellMap.get(date) ?? "AVAILABLE",
      inMonth: true,
    });
  }
  while (cellsOut.length % 7 !== 0) {
    const last = cellsOut[cellsOut.length - 1]!;
    const parsed = /^(\d{4})-(\d{2})-(\d{2})$/.exec(last.date);
    if (!parsed) break;
    const next = new Date(
      Date.UTC(Number(parsed[1]), Number(parsed[2]) - 1, Number(parsed[3]) + 1),
    );
    cellsOut.push({
      date: `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, "0")}-${String(next.getUTCDate()).padStart(2, "0")}`,
      state: "OUTSIDE_WINDOW",
      inMonth: false,
    });
  }
  return cellsOut;
}

function dayStateClass(state: DayStateKind): string {
  switch (state) {
    case "PAST":
      return "opacity-40 cursor-not-allowed";
    case "AVAILABLE":
      return "cursor-pointer hover:bg-accent/10";
    case "FULL":
      return "text-text/60 cursor-pointer";
    case "OUTSIDE_WINDOW":
      return "text-text/40 cursor-not-allowed";
    case "BLOCKED":
      return "cursor-pointer bg-[repeating-linear-gradient(45deg,rgba(182,130,53,0.18)_0_6px,transparent_6px_12px)] border border-accent/40";
  }
}

function formatDateLabel(date: string, locale: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return date;
  const dt = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return dt.toLocaleDateString(locale === "de" ? "de-DE" : "en-GB", {
    weekday: "long",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: "UTC",
  });
}

function shiftDay(date: string, delta: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return date;
  const next = new Date(
    Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + delta),
  );
  return `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, "0")}-${String(next.getUTCDate()).padStart(2, "0")}`;
}

export function ReservationsDatePicker({
  date,
  onChange,
  initiallyOpen = false,
  onOpenTagSperren,
}: {
  date: string;
  onChange: (next: string) => void;
  initiallyOpen?: boolean;
  onOpenTagSperren: () => void;
}) {
  const { t, i18n } = useTranslation();
  // Compute the month that contains the selected date so the grid
  // opens directly on the day the operator is editing.
  const [open, setOpen] = useState(initiallyOpen);
  const initialMonth = useMemo(() => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    return m ? `${m[1]}-${m[2]}` : currentMonthString(new Date());
  }, [date]);
  const [month, setMonth] = useState<string>(initialMonth);

  useEffect(() => {
    // Re-center the month when the selected date changes (e.g. arrow
    // buttons crossing a month boundary) without losing the manual
    // collapse state.
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    if (m) {
      const selectedMonth = `${m[1]}-${m[2]}`;
      if (selectedMonth !== month) setMonth(selectedMonth);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const availabilityQuery = useQuery({
    queryKey: ["reservationsDatePicker", month],
    queryFn: async () => {
      const res = await gqlRequest<{ availability: DayStateCell[] }>(
        AVAILABILITY_MONTH_QUERY,
        { month },
      );
      return res.availability;
    },
    enabled: open,
  });

  const cells = useMemo(
    () => availabilityQuery.data ?? [],
    [availabilityQuery.data],
  );
  const gridCells = useMemo(() => buildGridCells(month, cells), [month, cells]);
  const todayBerlin = formatInTimeZone(
    new Date(),
    "Europe/Berlin",
    "yyyy-MM-dd",
  );

  return (
    <div className="card p-3 mb-4" data-testid="reservations-date-picker">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          className="btn btn-outline btn-icon"
          onClick={() => onChange(shiftDay(date, -1))}
          aria-label={t("admin.reservations.datePicker.prevDay")}
          data-testid="reservations-date-prev"
        >
          ‹
        </button>
        <button
          type="button"
          className="flex-1 text-center font-heading text-base py-2"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="reservations-date-grid"
          data-testid="reservations-date-toggle"
        >
          {formatDateLabel(date, i18n.language)}
        </button>
        <button
          type="button"
          className="btn btn-outline btn-icon"
          onClick={() => onChange(shiftDay(date, 1))}
          aria-label={t("admin.reservations.datePicker.nextDay")}
          data-testid="reservations-date-next"
        >
          ›
        </button>
        <button
          type="button"
          className={`btn ${date === todayBerlin ? "btn-primary" : "btn-outline"}`}
          onClick={() => onChange(todayBerlin)}
          data-testid="reservations-date-today"
        >
          {t("admin.reservations.datePicker.today")}
        </button>
        <button
          type="button"
          className="btn btn-outline px-4 py-2"
          onClick={onOpenTagSperren}
          data-testid="reservations-tag-sperren"
        >
          <Lock className="h-4 w-4" aria-hidden="true" />
          <span>{t("admin.reservations.datePicker.blockDay")}</span>
        </button>
      </div>

      {open && (
        <div className="mt-3" data-testid="reservations-date-grid-panel">
          <div
            className="flex items-center justify-between gap-2 mb-2"
            data-testid="reservations-month-nav"
          >
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setMonth(shiftMonth(month, -1))}
              aria-label={t("admin.calendar.monthNav.prev")}
              data-testid="reservations-month-prev"
            >
              ‹
            </button>
            <span
              className="font-heading text-base"
              data-testid="reservations-month-label"
            >
              {formatMonthLabel(month)}
            </span>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setMonth(shiftMonth(month, 1))}
              aria-label={t("admin.calendar.monthNav.next")}
              data-testid="reservations-month-next"
            >
              ›
            </button>
          </div>
          <div
            className="grid grid-cols-7 gap-1"
            id="reservations-date-grid"
            data-testid="reservations-date-grid"
          >
            {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((wd) => (
              <div
                key={wd}
                className="text-text/60 text-xs uppercase text-center py-1"
              >
                {wd}
              </div>
            ))}
            {gridCells.map((cell) => {
              const isSelected = cell.date === date;
              const stateClass = dayStateClass(cell.state);
              return (
                <button
                  key={cell.date}
                  type="button"
                  onClick={() =>
                    cell.inMonth ? onChange(cell.date) : undefined
                  }
                  disabled={!cell.inMonth || cell.state === "PAST"}
                  data-testid={`reservations-date-cell-${cell.date}`}
                  data-day-state={cell.state}
                  className={`aspect-square flex items-center justify-center rounded-sm text-sm transition-colors ${stateClass} ${
                    isSelected ? "ring-2 ring-accent" : ""
                  } ${cell.inMonth ? "" : "opacity-30"}`}
                >
                  {cell.date.split("-")[2]}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
